import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  MapPin, 
  Filter, 
  Star,
  Eye, 
  Phone, 
  MessageCircle,
  Map as MapIcon,
  List,
  X,
  SlidersHorizontal,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useListings, useForexFilters, useLocationSearch, useRecordClick } from '@/hooks/useForex';
import { supportedCurrencies, nigerianCities } from '@/mocks/forexData';
import { formatDistanceToNow } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

const ForexListingsPage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const { filters, updateFilters, resetFilters } = useForexFilters();
  const { listings: apiListings, isLoading } = useListings(filters);
  const { locations } = useLocationSearch(locationQuery);
  const { recordClick } = useRecordClick();

  // Client-side search filtering
  const listings = useMemo(() => {
    if (!searchQuery.trim()) return apiListings;
    
    const query = searchQuery.toLowerCase();
    return apiListings.filter(listing => 
      listing.title.toLowerCase().includes(query) ||
      listing.location.city.toLowerCase().includes(query) ||
      listing.location.state.toLowerCase().includes(query) ||
      listing.currencies.some(c => 
        c.from.toLowerCase().includes(query) || 
        c.to.toLowerCase().includes(query)
      )
    );
  }, [apiListings, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowLocationDropdown(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Sync location query with filters
  useEffect(() => {
    if (filters.city) {
      setLocationQuery(filters.city);
    }
  }, [filters.city]);

  const handleLocationSelect = (city: typeof nigerianCities[0]) => {
    setLocationQuery(city.name);
    updateFilters({ city: city.name, state: city.state });
    setShowLocationDropdown(false);
    toast({ title: 'Location Filter Applied', description: `Showing results for ${city.name}, ${city.state}` });
  };

  const handleCurrencyChange = (currency: string) => {
    updateFilters({ currencyFrom: currency || undefined });
    if (currency) {
      toast({ title: 'Currency Filter Applied', description: `Showing exchangers for ${currency}` });
    }
  };

  const handleClearLocation = () => {
    setLocationQuery('');
    updateFilters({ city: undefined, state: undefined });
  };

  const handleContactClick = (e: React.MouseEvent, listingId: string, type: 'call' | 'whatsapp') => {
    e.stopPropagation();
    recordClick(listingId);
    
    // In a real app, this would get the actual vendor phone
    const phone = '+2348012345678';
    
    if (type === 'call') {
      window.open(`tel:${phone}`, '_blank');
    } else {
      const message = encodeURIComponent('Hi, I saw your listing on Boruda Exchange and I\'m interested in exchanging currency.');
      window.open(`https://wa.me/${phone.replace(/\+/g, '')}?text=${message}`, '_blank');
    }
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Currency Filter */}
      <div>
        <h4 className="font-semibold mb-3">Currency</h4>
        <div className="space-y-2">
          <select
            value={filters.currencyFrom || ''}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="">All Currencies</option>
            {supportedCurrencies.map((curr) => (
              <option key={curr.code} value={curr.code}>
                {curr.code} - {curr.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Verified Only */}
      <div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="verified"
            checked={filters.verifiedOnly}
            onCheckedChange={(checked) => {
              updateFilters({ verifiedOnly: checked as boolean });
              if (checked) {
                toast({ title: 'Filter Applied', description: 'Showing verified vendors only' });
              }
            }}
          />
          <label htmlFor="verified" className="text-sm font-medium cursor-pointer">
            Verified vendors only
          </label>
        </div>
      </div>

      {/* Sort By */}
      <div>
        <h4 className="font-semibold mb-3">Sort By</h4>
        <div className="space-y-2">
          {[
            { value: 'best_rate', label: 'Best Rate' },
            { value: 'nearest', label: 'Nearest' },
            { value: 'most_reviewed', label: 'Most Reviewed' },
            { value: 'newest', label: 'Newest' },
          ].map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="sortBy"
                value={option.value}
                checked={filters.sortBy === option.value}
                onChange={(e) => {
                  updateFilters({ sortBy: e.target.value as typeof filters.sortBy });
                  toast({ title: 'Sorting Applied', description: `Sorted by ${option.label}` });
                }}
                className="w-4 h-4"
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Reset Button */}
      <Button 
        variant="outline" 
        className="w-full" 
        onClick={() => {
          resetFilters();
          setSearchQuery('');
          setLocationQuery('');
          toast({ title: 'Filters Reset', description: 'All filters have been cleared' });
        }}
      >
        <X className="w-4 h-4 mr-2" />
        Reset Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors, currencies, locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Location */}
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Enter location..."
                value={locationQuery}
                onChange={(e) => {
                  setLocationQuery(e.target.value);
                  setShowLocationDropdown(true);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLocationDropdown(true);
                }}
                className="pl-10"
              />
              {locationQuery && (
                <button
                  onClick={handleClearLocation}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {showLocationDropdown && locations.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-auto">
                  {locations.map((city) => (
                    <button
                      key={city.name}
                      onClick={() => handleLocationSelect(city)}
                      className="w-full px-4 py-2 text-left hover:bg-accent flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{city.name}, {city.state}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Currency Select */}
            <div className="w-48">
              <select
                value={filters.currencyFrom || ''}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">All Currencies</option>
                {supportedCurrencies.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button (Mobile) */}
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>

            {/* View Toggle */}
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="icon"
                onClick={() => {
                  setViewMode('map');
                  toast({ title: 'Coming Soon', description: 'Map view will be available soon' });
                }}
              >
                <MapIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Active Filters */}
          {(filters.city || filters.currencyFrom || filters.verifiedOnly || searchQuery) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {searchQuery && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => setSearchQuery('')}>
                  Search: {searchQuery} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {filters.city && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilters({ city: undefined })}>
                  {filters.city} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {filters.currencyFrom && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilters({ currencyFrom: undefined })}>
                  {filters.currencyFrom} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {filters.verifiedOnly && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilters({ verifiedOnly: false })}>
                  Verified Only <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters (Desktop) */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                </h3>
              </div>
              <FilterContent />
            </div>
          </div>

          {/* Listings */}
          <div className="flex-1">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No listings found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your filters or search for a different location
                </p>
                <Button onClick={() => { resetFilters(); setSearchQuery(''); setLocationQuery(''); }}>
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-muted-foreground">
                    {listings.length} listing{listings.length !== 1 ? 's' : ''} found
                  </p>
                </div>

                <div className="space-y-4">
                  {listings.map((listing) => (
                    <Card 
                      key={listing.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/forex/listing/${listing.id}`)}
                    >
                      <CardContent className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row gap-4">
                          {/* Image */}
                          <div className="w-full md:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={listing.images[0] || '/placeholder-shop.jpg'}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h3 className="font-semibold text-lg mb-1">{listing.title}</h3>
                                <div className="flex items-center text-muted-foreground text-sm mb-2">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {listing.location.address}, {listing.location.city}
                                </div>
                              </div>
                              {listing.isFeatured && (
                                <Badge className="flex-shrink-0">
                                  <Star className="w-3 h-3 mr-1" />
                                  Featured
                                </Badge>
                              )}
                            </div>

                            {/* Rates */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                              {listing.currencies.slice(0, 4).map((curr) => (
                                <div key={`${curr.from}-${curr.to}`} className="bg-muted rounded-lg p-2">
                                  <div className="text-xs text-muted-foreground">
                                    {curr.from} → {curr.to}
                                  </div>
                                  <div className="font-semibold text-sm">
                                    ₦{curr.sellRate.toLocaleString()}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center">
                                  <Eye className="w-4 h-4 mr-1" />
                                  {listing.views} views
                                </span>
                                <span className="flex items-center">
                                  <Clock className="w-4 h-4 mr-1" />
                                  {formatDistanceToNow(new Date(listing.createdAt))}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => handleContactClick(e, listing.id, 'call')}
                                >
                                  <Phone className="w-4 h-4 mr-1" />
                                  Call
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={(e) => handleContactClick(e, listing.id, 'whatsapp')}
                                >
                                  <MessageCircle className="w-4 h-4 mr-1" />
                                  WhatsApp
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForexListingsPage;
